import React, { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContextWeb } from "../context/AuthContextWeb";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const roleTarget = {
  USER: "/app/search",
  RENTER: "/app/search",
  OWNER: "/owner/dashboard",
  ADMIN: "/owner/dashboard",
  SUPER_ADMIN: "/owner/dashboard",
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading, error, clearError } = useContext(AuthContextWeb);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLocalError("");
    clearError();

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !password) {
      setLocalError("Please enter your email and password.");
      return;
    }

    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setLocalError("Please enter a valid email address.");
      return;
    }

    try {
      const user = await login({ email: normalizedEmail, password });
      const normalizedRole = String(user?.role || "").toUpperCase();
      const roleBasedPath = roleTarget[normalizedRole] || "/app/search";
      const fromPath = location.state?.from?.pathname;
      // For owner/admin roles, only honor `from` if it's in the owner portal.
      // This prevents a saved renter path (/app/search, /app/account) from
      // overriding the correct owner redirect after login.
      const ownerRole = ["OWNER", "ADMIN", "SUPER_ADMIN"].includes(
        normalizedRole,
      );
      const redirectPath =
        fromPath && (!ownerRole || fromPath.startsWith("/owner"))
          ? fromPath
          : roleBasedPath;
      navigate(redirectPath, { replace: true });
    } catch {
      // API error surfaced through context
    }
  };

  return (
    <main className="app-surface">
      <section className="auth-card">
        <div className="auth-brand">
          <img
            src={process.env.PUBLIC_URL + "/assets/logo/LogoH.png"}
            alt="Rental Highway"
            className="auth-brand-logo"
          />
        </div>
        <h1>Sign in to Rental Highway</h1>
        <p>Access search and full property details on web.</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            disabled={loading}
          />

          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            disabled={loading}
          />

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        {(localError || error) && (
          <p className="form-error">{localError || error}</p>
        )}

        <p className="auth-footnote">
          New here? <Link to="/app/signup">Create an account</Link>
        </p>
        <p className="auth-footnote">
          <Link to="/">Back to homepage</Link>
        </p>
      </section>

      <p className="auth-below-card-link">
        Forgot your password? <Link to="/app/forgot-password">Reset it</Link>
      </p>
    </main>
  );
}
